import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Center,
  Spinner,
  Icon,
  chakra,
} from '@chakra-ui/react';
import { HiPlus, HiXMark, HiClock, HiCalendarDays } from 'react-icons/hi2';
import {
  Button,
  SelectInput,
  InputField,
  TextareaField,
  DialogComponent,
  StatusBadge,
} from '@themes/aegix';
import { useMyStudents } from '../hooks/useReunification';
import {
  guardianStudentName,
  type GuardianStudentWithLegacyIds,
} from '../services/reunification';
import {
  usePreCheckIns,
  useCreatePreCheckIn,
  useUpdatePreCheckIn,
} from '../hooks/useVisitorManagement';
import { toaster } from './ui/toaster';

// Helper to format ISO date to readable string
const formatDateTime = (isoString: string) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return isoString;
  }
};

// Helper to get local string for datetime-local value
const getLocalDatetimeString = (date: Date) => {
  const tzOffset = date.getTimezoneOffset() * 60000;
  const localISOTime = new Date(date.getTime() - tzOffset).toISOString();
  return localISOTime.slice(0, 16);
};

export function PreCheckInView() {
  const { t } = useTranslation();
  const { data: studentsData, isLoading: loadingStudents } = useMyStudents();
  const { data: preCheckInsData, isLoading: loadingPreCheckIns } = usePreCheckIns();

  const createMutation = useCreatePreCheckIn();
  const updateMutation = useUpdatePreCheckIn();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [expectedArrival, setExpectedArrival] = useState(() =>
    getLocalDatetimeString(new Date()),
  );
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The real guardian/students payload emits no `id`/`school_id`,
  // so this flow's invalid-data guard fires until a join key ships.
  const students: GuardianStudentWithLegacyIds[] = studentsData?.results ?? [];
  const preCheckIns = preCheckInsData?.results ?? [];

  const handleOpen = () => {
    if (students.length > 0) {
      setSelectedStudentId(String(students[0].id));
    }
    setExpectedArrival(getLocalDatetimeString(new Date()));
    setNotes('');
    setIsOpen(true);
  };

  const handleCancelPreCheckIn = async (publicId: string) => {
    try {
      await updateMutation.mutateAsync({
        public_id: publicId,
        data: { status: 'cancelled' },
      });
      toaster.create({
        title: t('preCheckin.cancelledToastTitle'),
        description: t('preCheckin.cancelledToastDesc'),
        type: 'success',
      });
    } catch {
      toaster.create({
        title: t('preCheckin.cancelErrorTitle'),
        description: t('preCheckin.cancelErrorDesc'),
        type: 'error',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudentId) {
      toaster.create({
        title: t('preCheckin.studentRequiredTitle'),
        description: t('preCheckin.studentRequiredDesc'),
        type: 'error',
      });
      return;
    }

    const student = students.find((s) => String(s.id) === selectedStudentId);
    if (!student || student.school_id === undefined || student.id === undefined) {
      toaster.create({
        title: t('preCheckin.invalidDataTitle'),
        description: t('preCheckin.invalidDataDesc'),
        type: 'error',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createMutation.mutateAsync({
        student: student.id,
        school: student.school_id,
        expected_arrival_at: new Date(expectedArrival).toISOString(),
        notes,
      });

      toaster.create({
        title: t('preCheckin.registeredTitle'),
        description: t('preCheckin.registeredDesc', { name: guardianStudentName(student) }),
        type: 'success',
      });
      setIsOpen(false);
    } catch {
      toaster.create({
        title: t('preCheckin.createErrorTitle'),
        description: t('preCheckin.createErrorDesc'),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const studentOptions = students.map((s) => ({
    label: guardianStudentName(s),
    value: String(s.id),
  }));

  const isLoading = loadingStudents || loadingPreCheckIns;

  return (
    <Box>
      <HStack justifyContent="space-between" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <Text fontSize="lg" fontWeight="600" color="text.primary">
            {t('preCheckin.title')}
          </Text>
          <Text fontSize="sm" color="text.secondary">
            {t('preCheckin.subtitle')}
          </Text>
        </Box>
        <Button
          visual="primary"
          onClick={handleOpen}
          icon={<HiPlus />}
          disabled={students.length === 0}
          data-e2e="add-pre-checkin-btn"
        >
          {t('preCheckin.addBtn')}
        </Button>
      </HStack>

      {isLoading ? (
        <Center py={12}>
          <Spinner size="lg" />
        </Center>
      ) : preCheckIns.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          {preCheckIns.map((p) => {
            const statusMap: Record<string, 'pending' | 'success' | 'inactive'> = {
              pending: 'pending',
              arrived: 'success',
              cancelled: 'inactive',
            };
            const badgeStatus = statusMap[p.status] ?? 'pending';

            return (
              <Box
                key={p.public_id}
                bg="surface.primary"
                border="1px solid"
                borderColor="border.secondary"
                borderRadius="lg"
                p={5}
                position="relative"
                data-e2e={`pre-checkin-card-${p.public_id}`}
              >
                <HStack justifyContent="space-between" mb={3} alignItems="flex-start">
                  <VStack align="stretch" gap={1}>
                    <Text fontSize="md" fontWeight="600" color="text.primary">
                      {p.student_name}
                    </Text>
                    <HStack fontSize="xs" color="text.secondary" gap={1.5}>
                      <Icon as={HiClock} />
                      <Text>{t('preCheckin.expected', { time: formatDateTime(p.expected_arrival_at) })}</Text>
                    </HStack>
                  </VStack>
                  <StatusBadge status={badgeStatus} label={p.status} />
                </HStack>

                {p.notes && (
                  <Box bg="surface.secondary" p={3} borderRadius="md" mb={4}>
                    <Text fontSize="xs" fontWeight="600" color="text.secondary" mb={1}>
                      {t('preCheckin.notes')}
                    </Text>
                    <Text fontSize="sm" color="text.primary">
                      {p.notes}
                    </Text>
                  </Box>
                )}

                {p.status === 'pending' && (
                  <HStack justifyContent="flex-end" mt={2}>
                    <Button
                      visual="secondary"
                      size="sm"
                      icon={<HiXMark />}
                      onClick={() => handleCancelPreCheckIn(p.public_id)}
                      data-e2e={`cancel-pre-checkin-${p.public_id}`}
                    >
                      {t('preCheckin.cancelBtn')}
                    </Button>
                  </HStack>
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      ) : (
        <Center py={12} border="1px dashed" borderColor="border.secondary" borderRadius="lg">
          <VStack gap={2}>
            <HiCalendarDays size={32} />
            <Text fontSize="md" fontWeight="600" color="text.secondary">
              {t('preCheckin.noActive')}
            </Text>
            <Text fontSize="sm" color="text.secondary" textAlign="center" maxW="400px">
              {students.length === 0
                ? t('preCheckin.noStudentsNotice')
                : t('preCheckin.planNextVisit')}
            </Text>
          </VStack>
        </Center>
      )}

      <DialogComponent
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        headingText={t('preCheckin.addBtn')}
        e2eTitle="add-pre-checkin-dialog"
      >
        <form onSubmit={handleSubmit}>
          <VStack gap={4} align="stretch" mt={4}>
            <SelectInput
              label={t('preCheckin.selectStudent')}
              name="student"
              option={studentOptions}
              value={selectedStudentId}
              onChange={setSelectedStudentId}
              e2eTitle="pre-checkin-student-select"
              searchPlaceholder={t('searchPlaceholder')}
              required
            />

            <Box>
              <chakra.label fontSize="sm" color="text.secondary" mb={1} display="block">
                {t('preCheckin.expectedArrival')}
              </chakra.label>
              <InputField
                type="datetime-local"
                value={expectedArrival}
                onChange={(e) => setExpectedArrival(e.target.value)}
                required
                data-e2e="pre-checkin-arrival-input"
              />
            </Box>

            <Box>
              <chakra.label fontSize="sm" color="text.secondary" mb={1} display="block">
                {t('preCheckin.notesLabel')}
              </chakra.label>
              <TextareaField
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('preCheckin.notesPlaceholder')}
                data-e2e="pre-checkin-notes-input"
              />
            </Box>

            <HStack justifyContent="flex-end" gap={3} mt={4}>
              <Button visual="secondary" onClick={() => setIsOpen(false)} type="button">
                {t('preCheckin.cancelBtn')}
              </Button>
              <Button visual="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('preCheckin.creating') : t('preCheckin.submit')}
              </Button>
            </HStack>
          </VStack>
        </form>
      </DialogComponent>
    </Box>
  );
}
